import _ from "lodash";
import {Dialog} from "web/tjenester/Dialog";
import {gettextCatalog} from "angular-gettext";
import {regNavn} from "felles/tjenester/tredjepartsTjenestenavn";
import {Filer} from "../../felles/tjenester/Filer";
import {IPromise, IQService} from "angular";


export class FilValidering {

    static regNavn = "FilValidering";


    static forInjisering() {
        return [
            Filer.regNavn,
            Dialog.regNavn,
            regNavn.ng.$q,
            regNavn.gettext.gettextCatalog,
            FilValidering
        ]
    }

    constructor(private filer: Filer,
                private dialog: Dialog,
                private $q:IQService,
                private gettextCatalog: gettextCatalog) {
    }

    // offentlige metoder

    /* Validerer både filtyper, filstørrelser og lesetilgang til fil.
     * Viser tilhørende dialoger om noen av sjekkene feiler
     */
    validerFilerFørOpplastingOgVisDialogHvisFeilet(filer: File[]) {
        // Valider for ugyldig filtype først, ikke vits å fortsette om denne ikke er ok.
        return this.validerFiltypeOgVisDialogHvisFeil(filer).then(filtyperErGyldige => {
            // tidlig retur om vi har forsøkt å laste opp en eller flere filer med ugyldig filtype, dialog vises
            if(!filtyperErGyldige) return this.$q.resolve(false);

            // Verifiser tilgang til filer og filstørrelser
            return this.validerFiltilgangOgStørrelseFørOpplasting(filer);
        });
    }

    // private metoder

    /** returnerer true vi støtter filtypene som forsøkes lastes opp */
    private validerFiltypeOgVisDialogHvisFeil(filer: File[]): IPromise<boolean> {
        const filerMedUlovligFiltype = filer.filter(fil => !this.filer.erTillattFiltype(fil.name));

        if (filerMedUlovligFiltype.length === 0) {
            return this.$q.resolve(true)
        }

        const dialogInnhold = this.gettextCatalog.getString('Filtype ikke støttet');
        const filnavn = _(filerMedUlovligFiltype).map(fil => `<li>${fil.name}</li>`).join('');

        return this.dialog.alertMedHtmlInnhold(this.gettextCatalog.getString('Opplasting avbrutt'), `<p>${dialogInnhold}</p> <ul>${filnavn}</ul>`).then(() => false);
    }

    /**
     * Returnerer true hvis alle filer er gyldige, dvs. kan leses, er større enn 0 og mindre enn maksstørrelse,
     *  false hvis filer er for store eller tomme eller ikke kan leses.
     *  ved 'false' vises dialog med informasjon til bruker.
     */
    private validerFiltilgangOgStørrelseFørOpplasting(filer: File[]): IPromise<boolean> {

        return this.validerFilerTilOpplasting(filer).then(ugyldigefiler => {
            if (ugyldigefiler.kanIkkeLeses.length === 0 && ugyldigefiler.tomme.length === 0 && ugyldigefiler.forStore.length === 0 ) {
                // Vi kan lese alle filene som kom inn, gjør ikke noe mer.
                return this.$q.resolve(true);
            }

            // Noen filer var tomme eller for store
            const filerMedUgyldigStørrelseInnhold = this.gettextCatalog.getString('Filer kan ikke være tomme eller større enn {{maxSize}}', {maxSize: this.filer.maxFilstørrelseILesbarForm()});
            const filnavn = [...ugyldigefiler.tomme, ...ugyldigefiler.forStore].map(fil => `<li>${fil.name} : ${this.filer.filStørreseILesbarForm(fil.size)} </li>`).join('');

            // Noen filer kunne leses fra filsystemet
            const filerSomIkkeKunneLesesInnhold = this.gettextCatalog.getString('Følgende filer kunne ikke leses');
            const filKunneIkkeLeses = ugyldigefiler.kanIkkeLeses.map(fil => `<li>${fil.name}</li>`).join('');
            const filerSomIkkeKunneLesesForklaring = this.gettextCatalog.getString("Filer kan ikke leses hvis de ikke er synkronisert til din enhet fra skylagring som Microsoft OneDrive, Google Drive e.l. Sjekk om filen(e) lar seg åpne lokalt og forsøk på nytt.");

            let htmlMal = this.harUgyldigFilstørrelse(ugyldigefiler) ? `<div class="e2e-modal-filer-ugyldigstorrelse"><p>${filerMedUgyldigStørrelseInnhold}</p> <ul>${filnavn}</ul></div>` : ''
            htmlMal += ugyldigefiler.kanIkkeLeses.length > 0 ? `<div class="e2e-modal-filer-kan-ikke-leses"><p>${filerSomIkkeKunneLesesInnhold}</p><ul>${filKunneIkkeLeses}</ul><div class="std-padding">${filerSomIkkeKunneLesesForklaring}</div></div>` : ''

            return this.dialog
              .alertMedHtmlInnhold(this.gettextCatalog.getString('Opplasting avbrutt'), htmlMal)
              .then(() => false)
        });
    }


    private validerFilerTilOpplasting(filer: File[]) :IPromise<FilOpplastingValidering> {
        const forStoreFiler = this.filer.filerSomErForStore(filer);
        const tommeFiler = this.filer.tommeFiler(filer);

        // valider så kun filer som fremdeles anses som gyldige for om de kan leses
        const filerSomSkalSjekkesForLesing = filer.filter(filkandidat => !forStoreFiler.includes(filkandidat) || !tommeFiler.includes(filkandidat));

        return this.filerSomIkkeKanLeses(filerSomSkalSjekkesForLesing).then(filerSomIkkeKunneLeses => {
            return {kanIkkeLeses: filerSomIkkeKunneLeses, tomme:tommeFiler, forStore:forStoreFiler};
        })
    }

    /**
     * Bruker FileReader apiet for å avgjøre om det er mulig å lese noe ut av en gitt fil
     * Brukes for å oppdage om f.eks. fil ikke er tilgjengelig via OneDrive e.l.
     * Da får man gjerne metadata om størrelse osv, men selve filinnholdet vil ikke være tilgjengelig.
     *
     * Merk denne kan ikke brukes av cordova og ligger derfor ikke i felles.
     */
    private filerSomIkkeKanLeses(filer:File[]): IPromise<File[]> {
        // map hver fil til et promise som sjekker filens lesbarhet
        const filLesesjekkPromiser = filer.map(fil => {
            // kan ikke lese tomme filer uansett
            if(fil.size === 0) this.$q.resolve({kanLeses:false, fil:fil});


            const deferred = this.$q.defer<{kanLeses:boolean, fil:File}>();

            // https://developer.mozilla.org/en-US/docs/Web/API/FileReader
            const fileReader = new FileReader();

            // forsøk å lese maks 1kB av filen
            const antallBytesÅLese = Math.min(fil.size, 1024);
            const blob = fil.slice(0, antallBytesÅLese);

            // overskriver eventhandlere og resolver promiset med forsøket på lesing

            // Fired when a read has completed successfully.
            fileReader.onload = () => deferred.resolve({kanLeses: true, fil: fil})

            // Fired when the read failed due to an error.
            fileReader.onerror = () => deferred.resolve({kanLeses: false, fil: fil})

            // Fired when a read has been aborted, for example because the program called FileReader.abort().
            fileReader.onabort = () => deferred.resolve({kanLeses: false, fil: fil})

            // gjør faktisk leseoperasjon
            fileReader.readAsArrayBuffer(blob);

            return deferred.promise;
        });

        // vent på alle fillese-forsøk promiser, returner så kun alle filer som _ikke_ kunne leses
        return this.$q.all(filLesesjekkPromiser).then(resultater => {
            return resultater.filter(resultat => !resultat.kanLeses).map(resultat => resultat.fil);
        });
    }

    private harUgyldigFilstørrelse(ugyldigefiler: FilOpplastingValidering) {
        return ugyldigefiler.tomme.length > 0 || ugyldigefiler.forStore.length > 0;
    }
}

export interface FilOpplastingValidering {
    forStore:File[];
    tomme:File[];
    kanIkkeLeses:File[];
}
